import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("sellers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", pin: "", email: "" });
  const [tempCred, setTempCred] = useState(null); // {username, pin}

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sellers = users.filter((u) => u.role === "seller");
  const customers = users.filter((u) => u.role === "customer");

  const createSeller = async () => {
    if (!form.name.trim()) { toast.error("أدخلي الاسم الظاهر"); return; }
    if (!/^[a-z0-9_]{3,20}$/.test(form.username.trim())) { toast.error("اسم المستخدم: 3-20 حرف إنجليزي/رقم"); return; }
    if (form.pin && !/^\d{6}$/.test(form.pin)) { toast.error("الرقم السري 6 أرقام"); return; }
    try {
      const { data } = await api.post("/admin/users", { name: form.name.trim(), username: form.username.trim().toLowerCase(), pin: form.pin.trim(), email: form.email.trim() });
      setTempCred({ username: data.username, pin: data.temp_pin });
      toast.success("تم إنشاء حساب العاملة");
      setOpen(false); setForm({ name: "", username: "", pin: "", email: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (u) => {
    try { await api.put(`/admin/users/${u.id}/${u.active ? "deactivate" : "activate"}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPin = async (u) => {
    if (!window.confirm(`إعادة تعيين الرقم السري لـ ${u.name}؟ سيتم إنهاء جلساتها الحالية.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, { pin: "" });
      setTempCred({ username: u.username, pin: data.temp_pin });
      toast.success("تم إعادة تعيين الرقم السري وإنهاء الجلسات القديمة");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast("تم النسخ"); };

  return (
    <div className="p-5 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl">المستخدمون</h1>
        {tab === "sellers" && (
          <button onClick={() => setOpen(true)} data-testid="add-seller-btn" className="flex items-center gap-2 bg-[#141110] text-white px-4 py-2.5 text-sm hover:bg-black transition-colors">
            <UserPlus className="w-4 h-4" /> إضافة عاملة
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("sellers")} data-testid="tab-sellers" className={`px-5 py-2 text-sm border ${tab === "sellers" ? "bg-[#141110] text-white border-[#141110]" : "bg-white border-border"}`}>العاملات ({sellers.length})</button>
        <button onClick={() => setTab("customers")} data-testid="tab-customers" className={`px-5 py-2 text-sm border ${tab === "customers" ? "bg-[#141110] text-white border-[#141110]" : "bg-white border-border"}`}>العميلات ({customers.length})</button>
      </div>

      {tempCred && (
        <div className="bg-amber-50 border border-amber-300 p-4 mb-6 text-sm" data-testid="temp-cred-box">
          <div className="font-medium mb-2">بيانات الدخول (تظهر مرة واحدة) — سلّميها للعاملة بشكل آمن:</div>
          <div className="flex items-center gap-2" dir="ltr">اسم المستخدم: <strong>{tempCred.username}</strong>
            <button onClick={() => copy(tempCred.username)} className="text-accent"><Copy className="w-3.5 h-3.5" /></button></div>
          <div className="flex items-center gap-2 mt-1" dir="ltr">الرقم السري: <strong>{tempCred.pin}</strong>
            <button onClick={() => copy(tempCred.pin)} className="text-accent"><Copy className="w-3.5 h-3.5" /></button></div>
          <button onClick={() => copy(`اسم المستخدم: ${tempCred.username}\nالرقم السري: ${tempCred.pin}`)} data-testid="copy-cred" className="mt-2 text-xs bg-[#141110] text-white px-3 py-1.5">نسخ بيانات الدخول</button>
          <button onClick={() => setTempCred(null)} className="text-xs underline mt-2 ms-3" data-testid="dismiss-cred">إخفاء</button>
        </div>
      )}

      {tab === "sellers" ? (
        <div className="grid gap-3 md:hidden">
          {sellers.map((u) => (
            <div key={u.id} className="bg-white border border-border p-4" data-testid={`user-card-${u.username}`}>
              <div className="flex justify-between items-start">
                <div><div className="font-medium">{u.name}</div><div className="text-xs text-text-muted" dir="ltr">@{u.username}</div></div>
                <span className={`text-xs ${u.active ? "text-green-700" : "text-destructive"}`}>{u.active ? "مفعّل" : "معطّل"}</span>
              </div>
              <div className="text-xs text-text-muted mt-1">آخر دخول: {u.last_login ? new Date(u.last_login).toLocaleString("ar-KW") : "—"}</div>
              <div className="flex gap-3 mt-3">
                <button onClick={() => resetPin(u)} data-testid={`reset-user-${u.username}`} className="text-accent underline text-xs"><KeyRound className="w-3 h-3 inline" /> إعادة تعيين الرقم</button>
                <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.username}`} className="text-accent underline text-xs">{u.active ? "تعطيل" : "تفعيل"}</button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className={`bg-white border border-border overflow-x-auto ${tab === "sellers" ? "hidden md:block" : ""}`}>
        <table className="w-full text-sm">
          <thead className="bg-surface text-text-muted text-xs">
            <tr>
              <th className="text-start p-3">الاسم الظاهر</th>
              <th className="text-start p-3">{tab === "sellers" ? "اسم المستخدم" : "البريد"}</th>
              <th className="text-start p-3">آخر دخول</th>
              <th className="text-start p-3">الحالة</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(tab === "sellers" ? sellers : customers).map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.username || u.email}`}>
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-text-muted" dir="ltr">{tab === "sellers" ? `@${u.username}` : u.email}</td>
                <td className="p-3 text-text-muted text-xs">{u.last_login ? new Date(u.last_login).toLocaleString("ar-KW") : "—"}</td>
                <td className="p-3"><span className={u.active ? "text-green-700" : "text-destructive"}>{u.active ? "مفعّل" : "معطّل"}</span></td>
                <td className="p-3 text-end whitespace-nowrap">
                  {u.role === "seller" && (
                    <>
                      <button onClick={() => resetPin(u)} data-testid={`reset-user-${u.username}`} className="text-accent underline text-xs me-3"><KeyRound className="w-3 h-3 inline" /> إعادة تعيين</button>
                      <button onClick={() => toggle(u)} data-testid={`toggle-user-${u.username}`} className="text-accent underline text-xs">{u.active ? "تعطيل" : "تفعيل"}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(tab === "sellers" ? sellers : customers).length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-text-muted">لا توجد حسابات</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-sm p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-2">إضافة عاملة جديدة</h3>
            <div><label className="text-xs text-text-muted">الاسم الظاهر (يظهر في الفاتورة والتقارير)</label>
              <input placeholder="مثال: عائشة" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="seller-name"
                className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" /></div>
            <div><label className="text-xs text-text-muted">اسم المستخدم (للدخول فقط، إنجليزي قصير)</label>
              <input placeholder="rnf1" dir="ltr" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} data-testid="seller-username"
                className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" /></div>
            <div><label className="text-xs text-text-muted">الرقم السري (6 أرقام — اتركيه فارغاً للتوليد التلقائي)</label>
              <input placeholder="123456" dir="ltr" inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} data-testid="seller-pin"
                className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" /></div>
            <div><label className="text-xs text-text-muted">البريد الإلكتروني (اختياري)</label>
              <input placeholder="—" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="seller-email"
                className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" /></div>
            <button onClick={createSeller} data-testid="save-seller-btn" className="w-full bg-[#141110] text-white py-3 text-sm hover:bg-black transition-colors">حفظ الحساب</button>
          </div>
        </div>
      )}
    </div>
  );
}
